import React, { useEffect, useState } from 'react';
import { FormControlLabel, Radio, RadioGroup } from '@material-ui/core';
import { useTranslation } from "react-i18next";
import ExitModalAndSave from '../Modal/ExitModalAndSave';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps, useHistory } from "react-router-dom";
import * as api from "../../api";
import Button from "../../Button/Button";
import { workOrderRepairObject } from '../../ducks/redux/actions/getAddress';
import {
  addHazardAssessmentAnswer,
  saveHazardGetQuestionsData,
  saveHazardPostAssessmentResponse,
  saveHHSRSAssessmentCompleted,
  savePreAssessmentOutcome,
  savePreAssessmentQuestionsData,
  savePreAssessmentReturnQuestion,
  updateHazardAssessmentAnswer,
} from '../../ducks/redux/actions/hazardAssessment';
import { HazardAssessmentResponse, HazardQuestion } from '../../models/ReferenceData/HazardAssessment';
import { WorksOrderRequest } from '../../models/WorksOrders/WorksOrderRequest';
import HeaderJourney from '../../storybook/Header/HeaderJourney';
import BackModal from '../Modal/BackModal';
import Message from '../../storybook/Message';
import Loader from 'react-loader-spinner';
import { Helper } from '../../utils/Helper/HelperUtil';

interface HazardPreassessmentProps extends RouteComponentProps<any> { }

const dependencyResponsesMatch = (
  configuredResponses: string[],
  actualCodes: string[],
  _sourceQuestionType?: string
): boolean => configuredResponses.some((code) => actualCodes.includes(code));

export const isDependencyMatched = (
  question: HazardQuestion,
  answers: { [key: string]: string[] },
  sourceQuestions: HazardQuestion[] = []
): boolean => {
  if (!question.dependsOn?.length) {
    return true;
  }

  return question.dependsOn.some((dependency) => {
    const actualCodes = answers[dependency.code] || [];
    const sourceQuestionType = sourceQuestions.find(
      (sourceQuestion) => sourceQuestion.code === dependency.code
    )?.type;

    return dependencyResponsesMatch(
      dependency.responses,
      actualCodes,
      sourceQuestionType
    );
  });
};

export const isDependencyMatchedForAnswer = (
  question: HazardQuestion,
  questionCode: string,
  answerCodes: string[],
  sourceQuestionType?: string
): boolean => question.dependsOn?.some((dependency) =>
  dependency.code === questionCode &&
  dependencyResponsesMatch(
    dependency.responses,
    answerCodes,
    sourceQuestionType
  )
) ?? false;

export const isPreAssessmentQuestion = (question: HazardQuestion): boolean =>
  Helper.isPreAssessmentCategory(question.category);

export const isMatchingPreAssessmentQuestion = (
  question: HazardQuestion,
  expectedCategory: string
): boolean => Helper.isMatchingPreAssessmentCategory(
  question.category,
  expectedCategory
);

export const getQuestionAlertMessage = (question?: HazardQuestion): string => {
  const alert = question?.alerts?.[0];
  if (!alert) {
    return "";
  }

  if (typeof alert.message === "string") {
    return alert.message;
  }

  return alert.message?.customer || alert.message?.default || "";
};

const getPreviousQuestionIndex = (
  questions: HazardQuestion[],
  currentIndex: number,
  answers: { [key: string]: string[] },
  sourceQuestions: HazardQuestion[]
) => {
  for (let index = currentIndex - 1; index >= 0; index--) {
    if (
      !questions[index].dependsOn?.length ||
      isDependencyMatched(questions[index], answers, sourceQuestions)
    ) {
      return index;
    }
  }

  return -1;
};

const getFirstApplicableQuestionIndex = (
  questions: HazardQuestion[],
  answers: { [key: string]: string[] },
  sourceQuestions: HazardQuestion[]
) => {
  const firstApplicableIndex = questions.findIndex(
    (question) =>
      !question.dependsOn?.length ||
      isDependencyMatched(question, answers, sourceQuestions)
  );

  return firstApplicableIndex === -1 ? 0 : firstApplicableIndex;
};

const HazardPreassessment: React.FC<HazardPreassessmentProps> = (props) => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const selectedRepairReason = useSelector((state: any) => state?.dashboardReducer?.formState?.selectedRepairReasonObject);
  const workOrderObject = useSelector((state: any) => state?.addresses?.workOrderRepair);
  const hazardResponses = useSelector((state: any) => state?.hazard?.responses || []);
  const cachedPreAssessmentJourneyQuestions = useSelector((state: any) => state?.hazard?.preAssessmentJourneyQuestions);
  const preAssessmentReturnQuestion = useSelector((state: any) => state?.hazard?.preAssessmentReturnQuestion || { code: "", from: "" });
  const isAdditionalNeedsUser = useSelector((state: any) => state?.userChoicesReducer?.hasAdditionalNeeds);
  const supportedResident = useSelector((state: any) => state?.userChoicesReducer?.isSupportedResident);
  const ActiveHazardCasePresent = useSelector((state: any) => state.hazard.hasActiveHazardCase);
  const activeHazardCase = useSelector((state: any) => state.hazard?.activeHazardCaseDetails);
  const [questions, setQuestions] = useState<HazardQuestion[]>([]);
  const [journeyQuestions, setJourneyQuestions] = useState<HazardQuestion[]>([]);
  const [answers, setAnswers] = useState<{ [key: string]: string[] }>({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isBackModalOpen, setIsBackModalOpen] = useState(false);
  const currentQuestion = questions[currentIndex];
  const selectedAnswers = currentQuestion ? answers[currentQuestion.code] || [] : [];
  const diagnosticId = selectedRepairReason?.[0]?.diagnosticId;
  const preAssessmentCategory = diagnosticId !== undefined && diagnosticId !== null
    ? `${Helper.PreAssessmentCategoryPrefix}-${diagnosticId}`
    : "";
  const alertMessage = getQuestionAlertMessage(currentQuestion);
  const selectedproperty = useSelector(
    (state: any) => state?.dashboardReducer?.formState?.tracks?.[0]?.assetType
  );

  useEffect(() => {
    if (selectedRepairReason?.[0]?.isPreAssessmentRequired !== true && selectedRepairReason?.[0]?.isHazard === true) {
      props.history.push("/HazardAssessment/1");
      return;
    }

    const loadPreAssessmentQuestions = async () => {
      try {
        setIsLoading(true);
        if (!preAssessmentCategory || !Array.isArray(cachedPreAssessmentJourneyQuestions)) {
          setQuestions([]);
          setJourneyQuestions([]);
          dispatch(saveHazardGetQuestionsData([]));
          return;
        }

        const allQuestions: HazardQuestion[] = cachedPreAssessmentJourneyQuestions;

        const normalisedQuestions = allQuestions.map((question) => ({
          ...question,
          options: [...question.options].sort((first, second) =>
            first.code.localeCompare(second.code, undefined, { numeric: true })
          ),
        }));
        const preAssessmentQuestions = normalisedQuestions
          .filter((question) =>
            isMatchingPreAssessmentQuestion(question, preAssessmentCategory)
          )
          .sort((first, second) => first.sequence - second.sequence);
        const assessmentQuestions = normalisedQuestions
          .filter((question) => !isPreAssessmentQuestion(question))
          .sort((first, second) => first.sequence - second.sequence);

        setJourneyQuestions(normalisedQuestions);
        setQuestions(preAssessmentQuestions);
        dispatch(saveHazardGetQuestionsData(assessmentQuestions));

        const preAssessmentQuestionCodes = new Set(preAssessmentQuestions.map((question) => question.code));
        const restoredAnswers = hazardResponses.reduce((result: { [key: string]: string[] }, item: any) => {
          if (item.isPreAssessment && item.answerCodes?.length && preAssessmentQuestionCodes.has(item.code)) {
            result[item.code] = [...item.answerCodes];
          }
          return result;
        }, {});

        const existingAssessmentIdentifier =
          workOrderObject?.assessmentInformation?.identifier ||
          workOrderObject?.assessmentIdentifier;

        if (existingAssessmentIdentifier && !Object.keys(restoredAnswers).length) {
          try {
            const savedAssessment = await api.getHazardAssessment(existingAssessmentIdentifier);

            savedAssessment?.responses?.forEach((savedResponse) => {
              const question = normalisedQuestions.find((item) => item.code === savedResponse.code);
              if (!question || !savedResponse.answers?.length) {
                return;
              }

              const payload = {
                sequence: question.sequence,
                question: question.label,
                answers: question.options
                  .filter((option) => savedResponse.answers.includes(option.code))
                  .map((option) => option.text),
                answerCodes: [...savedResponse.answers],
                code: question.code,
                isPreAssessment: isPreAssessmentQuestion(question),
              };
              const existingQuestion = hazardResponses.some((item: any) => item.code === question.code);
              dispatch(existingQuestion ? updateHazardAssessmentAnswer(payload) : addHazardAssessmentAnswer(payload));

              if (payload.isPreAssessment) {
                restoredAnswers[question.code] = [...savedResponse.answers];
              }
            });
            if (savedAssessment?.outcome) {
              dispatch(saveHazardPostAssessmentResponse({
                id: savedAssessment.id,
                hazardType: savedAssessment.hazardType,
                ...savedAssessment.outcome,
              }));
            }
          } catch (e) {
            console.error(e);
          }
        }

        preAssessmentQuestions.forEach((question) => {
          if (question.dependsOn?.length && restoredAnswers[question.code]?.length && !isDependencyMatched(question, restoredAnswers, normalisedQuestions)) {
            const existingResponse = hazardResponses.find((item: any) => item.code === question.code);
            if (existingResponse) {
              dispatch(updateHazardAssessmentAnswer({ ...existingResponse, answers: [], answerCodes: [] }));
            }
            delete restoredAnswers[question.code];
          }
        });

        setAnswers(restoredAnswers);
        const canUseReturnQuestion = Boolean(preAssessmentReturnQuestion?.from);
        const returnQuestionIndex = canUseReturnQuestion
          ? preAssessmentQuestions.findIndex((question) =>
            question.code === preAssessmentReturnQuestion.code &&
            (!question.dependsOn?.length || isDependencyMatched(question, restoredAnswers, normalisedQuestions))
          )
          : -1;
        setCurrentIndex(returnQuestionIndex !== -1
          ? returnQuestionIndex
          : getFirstApplicableQuestionIndex(preAssessmentQuestions, restoredAnswers, normalisedQuestions));
        if (canUseReturnQuestion) {
          dispatch(savePreAssessmentReturnQuestion({ code: "", from: "" }));
        }
      } catch (e) {
        console.error(e);
        setQuestions([]);
        setJourneyQuestions([]);
        dispatch(saveHazardGetQuestionsData([]));
      } finally {
        setIsLoading(false);
      }
    };

    loadPreAssessmentQuestions();
  }, []);

  const saveResponse = (question: HazardQuestion, answerCodes: string[]) => {
    const payload = {
      sequence: question.sequence,
      question: question.label,
      answers: question.options
        .filter((response) => answerCodes.includes(response.code))
        .map((response) => response.text),
      answerCodes: [...answerCodes],
      code: question.code,
      isPreAssessment: true,
    };
    const existingQuestion = hazardResponses.some((item: any) => item.code === question.code);
    dispatch(existingQuestion ? updateHazardAssessmentAnswer(payload) : addHazardAssessmentAnswer(payload));
    return payload;
  };

  const persistStandardOutcome = async (updatedResponses: any[]) => {
    const request = {
      assetType: selectedproperty || "",
      hazardType: selectedRepairReason?.[0]?.hazardType || "",
      contractorRating: null,
      hasAdditionalNeeds: Boolean(isAdditionalNeedsUser),
      isSupportedResident: Boolean(supportedResident),
      responses: updatedResponses
        .filter((item: any) => item.isPreAssessment && item.answerCodes?.length)
        .map((item: any) => ({ code: item.code, answers: [...item.answerCodes] })),
    };

    let assessmentOutcome: HazardAssessmentResponse;
    const existingAssessmentIdentifier =
      workOrderObject?.assessmentInformation?.identifier ||
      workOrderObject?.assessmentIdentifier;

    if (existingAssessmentIdentifier) {
      assessmentOutcome = await api.updateHazardAssessment(existingAssessmentIdentifier, request);
    } else {
      assessmentOutcome = (await api.createHazardAssessment(request))?.[0];
    }

    if (!assessmentOutcome?.id) {
      throw new Error("Hazard assessment identifier was not returned");
    }

    if (existingAssessmentIdentifier && assessmentOutcome.id !== existingAssessmentIdentifier) {
      throw new Error("Hazard assessment identifier changed");
    }

    const assessmentType = assessmentOutcome.hazardType || request.hazardType;
    if (!assessmentType) {
      throw new Error("Hazard assessment type was not returned");
    }

    workOrderObject.assessmentIdentifier = assessmentOutcome.id;
    workOrderObject.assessmentInformation = {
      ...workOrderObject.assessmentInformation,
      type: assessmentType,
      identifier: assessmentOutcome.id,
      hasAdditionalNeeds: Boolean(isAdditionalNeedsUser),
      IsSupportedResident: Boolean(supportedResident),
    };
    workOrderObject.isHHSRSHazard = false;
    workOrderObject.hhsrsAssessment = undefined;

    const worksOrderRequest = new WorksOrderRequest(workOrderObject);
    await api.updateWorkOrderRepair(
      worksOrderRequest.updateWorkOrderParameter(workOrderObject),
      workOrderObject.id,
      "eTag"
    );
    dispatch(workOrderRepairObject(workOrderObject));
    dispatch(saveHazardPostAssessmentResponse(assessmentOutcome));
    dispatch(savePreAssessmentOutcome(assessmentOutcome.risk?.overall ?? ""));
    dispatch(saveHHSRSAssessmentCompleted(false));
  };

  const handleContinueButton = async () => {
    dispatch(saveHHSRSAssessmentCompleted(false));
    if (!currentQuestion) {
      return;
    }

    if (selectedAnswers.length === 0) {
      setError("Pre_Assessment_Select_Answer_Error");
      return;
    }

    setError("");
    const payload = saveResponse(currentQuestion, selectedAnswers);
    const currentAnswers = { ...answers, [currentQuestion.code]: [...selectedAnswers] };
    const updatedResponses = [
      ...questions
        .filter((question, index) => index <= currentIndex && currentAnswers[question.code]?.length)
        .map((question) => question.code === currentQuestion.code ? payload : ({
          sequence: question.sequence,
          question: question.label,
          answers: question.options
            .filter((response) => currentAnswers[question.code].includes(response.code))
            .map((response) => response.text),
          answerCodes: [...currentAnswers[question.code]],
          code: question.code,
          isPreAssessment: true,
        })),
    ];

    questions.slice(currentIndex + 1).forEach((question) => {
      if (question.dependsOn?.length && !isDependencyMatched(question, currentAnswers, journeyQuestions)) {
        const existingResponse = hazardResponses.find((item: any) => item.code === question.code);
        if (existingResponse?.answerCodes?.length) {
          dispatch(updateHazardAssessmentAnswer({ ...existingResponse, answers: [], answerCodes: [] }));
        }
        delete currentAnswers[question.code];
      }
    });

    const nextQuestionIndex = questions.findIndex((question, index) =>
      index > currentIndex &&
      isDependencyMatchedForAnswer(
        question,
        currentQuestion.code,
        selectedAnswers,
        currentQuestion.type
      )
    );

    const assessmentQuestions = journeyQuestions
      .filter((question) => !isPreAssessmentQuestion(question))
      .sort((first, second) => first.sequence - second.sequence);
    const matchingAssessmentQuestionIndex = assessmentQuestions.findIndex((question) =>
      isDependencyMatchedForAnswer(
        question,
        currentQuestion.code,
        selectedAnswers,
        currentQuestion.type
      )
    );

    const isMultiSelectQuestion = currentQuestion.type === "CHECKBOX";

    if (matchingAssessmentQuestionIndex !== -1 && (isMultiSelectQuestion || nextQuestionIndex === -1)) {
      if (ActiveHazardCasePresent) {
        workOrderObject.caseIdentifier = activeHazardCase?.identifiers?.caseIdentifier;
        workOrderObject.hazardSorCode = selectedRepairReason?.[0]?.hazardSorCode;
        await persistStandardOutcome(updatedResponses);
        props.history.push("/uploadmedia");
      }
      else {
        props.history.push(`/HazardAssessment/${matchingAssessmentQuestionIndex + 1}`);
      }
      return;
    }

    if (nextQuestionIndex !== -1) {
      setAnswers(currentAnswers);
      setCurrentIndex(nextQuestionIndex);
      return;
    }

    try {
      setIsLoading(true);
      if (ActiveHazardCasePresent) {
        workOrderObject.caseIdentifier = '';
      }
      workOrderObject.hazardSorCode = 'NA';
      await persistStandardOutcome(updatedResponses);
      props.history.push("/uploadmedia");
    } catch (e) {
      console.error(e);
      props.history.push("/GenericError");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRadioChange = (code: string) => {
    setAnswers((previousAnswers) => ({
      ...previousAnswers,
      [currentQuestion.code]: code ? [code] : [],
    }));
    setError("");
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, code: string) => {
    const currentAnswers = answers[currentQuestion.code] || [];
    const updatedAnswers = event.target.checked
      ? [...currentAnswers, code]
      : currentAnswers.filter((answerCode) => answerCode !== code);

    setAnswers((previousAnswers) => ({
      ...previousAnswers,
      [currentQuestion.code]: updatedAnswers,
    }));
    setError("");
  };

  const backButtonHandle = () => {
    setIsBackModalOpen(false);
    setError("");
    const previousIndex = getPreviousQuestionIndex(questions, currentIndex, answers, journeyQuestions);
    if (previousIndex !== -1) {
      setCurrentIndex(previousIndex);
    } else {
      props.history.push("/RepairAdditionalDetails");
    }
  };

  const exitButtonHandle = () => {
    dispatch(savePreAssessmentQuestionsData([]));
    history.push("/customer360View");
  }
  const saveDraftAndExit = () => {
    dispatch(savePreAssessmentQuestionsData([]));
    props.history.push("/customer360View");
  };
  const renderCurrentQuestion = () => {
    if (!currentQuestion) {
      return null;
    }

    const isMultiSelect = currentQuestion.type === "CHECKBOX";
    const isDropdown = !isMultiSelect && currentQuestion.options.length > 3;

    return (
      <div className="mb-4">
        <h3 className="header-size pb-12">
          {currentQuestion.label}
        </h3>

        {isDropdown && (
          <select
            aria-label={currentQuestion.code}
            value={selectedAnswers[0] || ""}
            onChange={(event) => handleRadioChange(event.target.value)}
            className="block dropDownWidth rounded border border-grey mb-4 bg-grey"
          >
            <option value="">Select an option</option>
            {currentQuestion.options.map((option) => (
              <option key={option.code} value={option.code}>{option.text}</option>
            ))}
          </select>
        )}

        {!isMultiSelect && !isDropdown && (
          <div className="mt-2">
            <RadioGroup
              id={`${currentQuestion.code}-radiogroup`}
              name={currentQuestion.code}
              value={selectedAnswers[0] || ""}
              onChange={(_event, value) => handleRadioChange(value)}
              aria-label={currentQuestion.label}
            >
              {currentQuestion.options.map((option, index) => (
                <FormControlLabel
                  key={option.code}
                  value={option.code}
                  control={<Radio id={`${currentQuestion.code}-radio${index}`} color="primary" />}
                  label={option.text}
                  id={`${currentQuestion.code}-label${index}`}
                />
              ))}
            </RadioGroup>
          </div>
        )}

        {isMultiSelect && (
          <div className="grid grid-cols-1 gap-2 dropDownWidth mt-2">
            {currentQuestion.options.map((option) => {
              const isChecked = selectedAnswers.includes(option.code);
              return (
                <label
                  key={option.code}
                  htmlFor={`${currentQuestion.code}-${option.code}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "10px 14px",
                    borderRadius: "8px",
                    border: isChecked ? "1px solid #0B8B84" : "1px solid #d1d5db",
                    backgroundColor: isChecked ? "#E6F6F5" : "#ffffff",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    minHeight: "48px",
                  }}
                >
                  <input
                    type="checkbox"
                    id={`${currentQuestion.code}-${option.code}`}
                    name={currentQuestion.code}
                    className="checkPos flex-shrink-0"
                    style={{ width: "18px", height: "18px", accentColor: "#0B8B84" }}
                    value={option.code}
                    checked={isChecked}
                    onChange={(event) => handleCheckboxChange(event, option.code)}
                    aria-label={option.text}
                  />
                  <span className="ml-8 text-sm w-90 inline-block">{option.text}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>
    );
  };



  return (
    <>
      <HeaderJourney></HeaderJourney>
      <div className="container-fluid parent-footer repair-request p-0" id="repAddDet-container">
        {isLoading && (
          <div className="pl-2">
            <Loader
              type="ThreeDots"
              color="#00BFFF"
              height={50}
              width={50}
            />
          </div>
        )
        }
        <div
          className="padding-repair"
        >
          {currentQuestion && currentIndex === 0 && alertMessage && (
            <div role="alert" className="alert mb-8 p-0 ml-2">
              <Message className="info-msg">
                {alertMessage}
              </Message>
            </div>)}

          {renderCurrentQuestion()}

          {error !== "" &&
            <Message id="hhsrsPreQues" severity="error" className="error-msg">
              {t("Pre_Assessment_Select_Answer_Error")}
            </Message>
          }
        </div>
        <div className="col-md-12 marginTop-auto" id="repAddDet-div5">

          <div className="row m-0 footer-actionbtn">
            <div className="col btn-top padding-repair mb-0">



              <BackModal
                id="repAddDet-backmodal"
                back={backButtonHandle}
                isOpen={isBackModalOpen}
                setIsOpen={setIsBackModalOpen}
              />
              <ExitModalAndSave
                id="assesement-exitmodal"
                exit={exitButtonHandle}
                saveDraftAndExit={saveDraftAndExit}
              />
            </div>
            <div className="col text-end pt-24 padding-repair">
              <Button
                id="btn_hhrs_continue"
                onClick={handleContinueButton}
                className="primary-btn"
              >
                {t("continuen_button")}{" "}
              </Button>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}
export default HazardPreassessment;